/**
 * Servicio para manejar operaciones relacionadas con información de empresa
 * Utiliza la nueva API de empresa del backend
 */

import axios from 'axios';

/**
 * Obtiene la información completa de la empresa del usuario autenticado
 * @returns {Promise<Object>} - Información de la empresa
 */
export const getEmpresaInfo = async () => {
  try {
    console.log(`🔍 [empresaService] Obteniendo información de empresa...`);
    
    const response = await axios.get(`/api/empresa/info`, {
      timeout: 10000,
      headers: { Accept: 'application/json' }
    });
    
    if (response.data.Status === 'Success') {
      console.log(`✅ [empresaService] Información obtenida:`, response.data.data);
      return response.data;
    } else {
      throw new Error(response.data.Error || 'Error desconocido al obtener información de empresa');
    }
  } catch (error) {
    console.error(`❌ [empresaService] Error obteniendo información de empresa:`, error);
    
    // Manejar diferentes tipos de errores
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          throw new Error('No autorizado. Por favor, inicie sesión nuevamente.');
        case 404:
          throw new Error('Información de empresa no encontrada.');
        case 500:
          throw new Error('Error interno del servidor.');
        default:
          throw new Error(data.Error || 'Error desconocido al obtener información de empresa');
      }
    }
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Tiempo de espera agotado. Verifique su conexión.');
    }
    
    throw error;
  }
};

/**
 * Actualiza la información de la empresa del usuario autenticado
 * @param {Object} empresaData - Datos de la empresa a actualizar
 * @returns {Promise<Object>} - Respuesta de la actualización
 */
export const updateEmpresaInfo = async (empresaData) => {
  try {
    console.log(`💾 [empresaService] Actualizando información de empresa...`, empresaData);
    
    const response = await axios.put(`/api/empresa/info`, empresaData, {
      timeout: 15000,
      headers: { 
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    });
    
    if (response.data.Status === 'Success') {
      console.log(`✅ [empresaService] Información actualizada exitosamente`);
      return response.data;
    } else {
      throw new Error(response.data.Error || 'Error desconocido al actualizar información de empresa');
    }
  } catch (error) {
    console.error(`❌ [empresaService] Error actualizando información de empresa:`, error);
    
    // Manejar diferentes tipos de errores
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          throw new Error('Datos inválidos proporcionados.');
        case 401:
          throw new Error('No autorizado. Por favor, inicie sesión nuevamente.');
        case 404:
          throw new Error('Empresa no encontrada.');
        case 500:
          throw new Error('Error interno del servidor.');
        default:
          throw new Error(data.Error || 'Error desconocido al actualizar información de empresa');
      }
    }
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Tiempo de espera agotado. Verifique su conexión.');
    }
    
    throw error;
  }
};

/**
 * Valida los datos de empresa antes de enviarlos al servidor
 * @param {Object} empresaData - Datos de la empresa a validar
 * @returns {Object} - Resultado de la validación
 */
export const validateEmpresaData = (empresaData) => {
  const errors = [];
  
  // Validaciones básicas
  if (empresaData.ruc && !/^\d{11}$/.test(empresaData.ruc)) {
    errors.push('El RUC debe tener exactamente 11 dígitos');
  }
  
  if (empresaData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(empresaData.email)) {
    errors.push('El email no tiene un formato válido');
  }
  
  if (empresaData.telefono && !/^\+?[\d\s-()]+$/.test(empresaData.telefono)) {
    errors.push('El teléfono no tiene un formato válido');
  }
  
  // Validar URLs
  const urlFields = ['pagina_web'];
  if (empresaData.redes_sociales) {
    urlFields.push(...Object.keys(empresaData.redes_sociales).filter(key => key !== 'whatsapp'));
  }
  
  urlFields.forEach(field => {
    const value = field === 'pagina_web' ? empresaData[field] : empresaData.redes_sociales?.[field];
    if (value && !isValidUrl(value)) {
      errors.push(`${field} no tiene un formato de URL válido`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors: errors
  };
};

/**
 * Función helper para validar URLs
 * @param {string} url - URL a validar
 * @returns {boolean} - True si es válida
 */
const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Obtiene la configuración de departamentos disponibles
 * @returns {Array} - Lista de departamentos
 */
export const getDepartamentos = () => {
  return [
    'Lima',
    'Arequipa',
    'La Libertad',
    'Piura',
    'Lambayeque',
    'Junín',
    'Cusco',
    'Ancash',
    'Ica',
    'Huancavelica',
    'Ayacucho',
    'Cajamarca',
    'Puno',
    'Loreto',
    'Ucayali',
    'Huánuco',
    'San Martín',
    'Tacna',
    'Apurímac',
    'Tumbes',
    'Pasco',
    'Moquegua',
    'Amazonas',
    'Madre de Dios',
    'Callao'
  ].sort();
};

/**
 * Formatea los datos de empresa para mostrar en el frontend
 * @param {Object} empresaData - Datos raw de la empresa
 * @returns {Object} - Datos formateados
 */
export const formatEmpresaData = (empresaData) => {
  return {
    ...empresaData,
    redes_sociales: empresaData.redes_sociales || {
      facebook: '',
      instagram: '',
      whatsapp: '',
      youtube: '',
      linkedin: '',
      tiktok: ''
    }
  };
};

export default {
  getEmpresaInfo,
  updateEmpresaInfo,
  validateEmpresaData,
  getDepartamentos,
  formatEmpresaData
};
